import { esES } from '@mui/material/locale';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Palette Definitions - High Contrast & Sobriety
const PRIMARY_MAIN = '#0f766e'; // Data Teal - Modern, Analytic, "Moneyball"
const BACKGROUND_DEFAULT = '#f0f2f5'; // Light Gray - Clean Professional
const TEXT_PRIMARY = '#03100fff'; // Teal 900 - Deep, rich text for better harmony than pure black
const TEXT_SECONDARY = '#605c58ff'; // Burnished Gold - Richer, Metallic
const HOVER = '#e2e8f0'; // Slate 200
const DIVIDER = '#cbd5e1'; // Slate 300 - Stronger borders
const COLOR_TABLAS = '#edeff3ff';
const COLOR_TABLAS_SUAVE = '#f5f7f9ff';
const COLOR_BOTONES = '#f0f3f5ff';

let theme = createTheme(
  {
    palette: {
      custom: {
        tablas: COLOR_TABLAS,
        tablasSuave: COLOR_TABLAS_SUAVE,
        botones: COLOR_BOTONES,
      },
      primary: {
        main: PRIMARY_MAIN,
        dark: '#115e59',
        contrastText: '#ffffff',
      },
      background: {
        main: BACKGROUND_DEFAULT,
      },
      text: {
        primary: TEXT_PRIMARY,
        secondary: TEXT_SECONDARY,
      },
      hover: {
        main: HOVER,
      },
      divider: DIVIDER,
      action: {
        hover: HOVER,
      },
      success: {
        main: '#bae8d9ff', // Emerald 600 - Darker for contrast
        light: '#ddfcecff', // Emerald 100
        dark: '#14b8a6', // Emerald 800
        medium: '#97e6caab',
      },
      fail: {
        main: '#dc2626', // Red 600
        light: '#fee2e2', // Red 100
      },
      info: {
        main: '#0ea5e9', // Blue 500
        light: '#bae6fd', // Blue 50
        dark: '#0284c7', // Blue 800
      },
      default: {
        main: '#e1e7e5ff',
      },
      highlight: {
        main: '#fef3c7', // Amber 100
      },
      categoria: {
        plus3: '#ccf1e8ff', // Teal 200
        plus2: '#e2f7f3ff', // Teal 100
        plus1: '#e2f7f3ff', // Teal 50
        minus1: '#fff1f2', // Rose 50
        minus2: '#ffe4e6', // Rose 100
        default: 'transparent',
      },
    },

    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: TEXT_PRIMARY,
        letterSpacing: '-0.025em',
      },
      h2: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: TEXT_PRIMARY,
        letterSpacing: '-0.025em',
      },
      h3: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: TEXT_PRIMARY,
      },
      h4: {
        fontSize: '1rem',
        fontWeight: 500,
        color: TEXT_PRIMARY,
      },
      text: {
        fontSize: '0.95rem',
        fontWeight: 400,
        color: TEXT_PRIMARY,
      },
      textSmall: {
        fontSize: '0.8rem',
        fontWeight: 500,
        color: TEXT_SECONDARY,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },

    spacing: 8,
    borderRadius: 8, // Compact

    shadows: [
      'none',
      '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // Micro shadow
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)', // Crisp elevation
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // Extra deep for modals
      ...Array(19).fill('none'),
    ],

    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: PRIMARY_MAIN,
            color: '#fff',
            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.2)', // Slight definition
            borderBottom: `1px solid #000`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: HOVER,
              // Subtle contour shift could be done here if needed
            },
          },
          containedPrimary: {
            backgroundColor: '#14b8a6', // Teal 500 - Softer, friendlier
            color: '#fff',
            '&:hover': {
              backgroundColor: '#0f766e', // Teal 700 - Darker on hover
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
            border: `1px solid ${DIVIDER}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${DIVIDER}`,
          },
          head: {
            fontWeight: 700,
            color: TEXT_PRIMARY,
            backgroundColor: '#f1f5f9', // Slate 100 - Soft Gray Header
            borderTop: `1px solid ${DIVIDER}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset, & .MuiOutlinedInput-notchedOutline': {
              borderColor: DIVIDER, // Standard border
            },
            '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: TEXT_SECONDARY, // Darker on hover
            },
            '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: DIVIDER, // Retain gray border on focus
              borderWidth: '1px', // Keep thin
            },
          },
        },
      },
      MuiTablePagination: {
        defaultProps: {
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    fontSize: '0.8rem',
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    minHeight: 'auto',
                    py: 0.5,
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  esES
);

theme = responsiveFontSizes(theme);

export default theme;
